/// <reference path="../../svg.d.ts" />
import React from 'react';
import { ItemType } from '@hero/schema';
import { getIconProps, IconProps, ICON } from '../../utils';
import PackageSvg from '../../assets/packing/package.svg?react';
import ChemicalsSvg from '../../assets/items/chemicals.svg?react';
import MedicalSuppliesSvg from '../../assets/items/medical_supplies.svg?react';
import FoodSvg from '../../assets/items/food.svg?react';
import LiquidSvg from '../../assets/items/liquid.svg?react';
import PerfumeSvg from '../../assets/items/perfume.svg?react';
import BeautySvg from '../../assets/items/beauty.svg?react';
import ConstructionSvg from '../../assets/items/construction.svg?react';
import PlasticSvg from '../../assets/items/plastic.svg?react';
import FurnitureSvg from '../../assets/items/furniture.svg?react';

// Helper function to scale stroke-width for icons with non-standard viewBox sizes
// Standard icons use 24x24 viewBox, but some custom SVGs use larger viewBoxes (e.g., 682x682)
// This scales the stroke-width proportionally to maintain visual consistency
const VIEWBOX_SCALE = 500 / 24;
const getScaledStrokeWidth = (
  iconProps: React.SVGProps<SVGSVGElement>,
  viewboxScale = VIEWBOX_SCALE,
) => {
  const baseStrokeWidth =
    typeof iconProps.strokeWidth === 'number' ? iconProps.strokeWidth : ICON.strokeWidth;
  return baseStrokeWidth * viewboxScale;
};

// ItemMainIcon - uses package icon as the main/default item icon
export const ItemMainIcon: React.FC<IconProps> = props => {
  return <PackageSvg {...getIconProps(props)} />;
};

export const PerfumeIcon: React.FC<IconProps> = props => {
  // Note: PerfumeIcon uses a custom SVG with viewBox 682x682 (vs standard 24x24)
  // We scale stroke-width proportionally: 1.5 * (500/24) ≈ 31.25
  // This is a one-time calculation - if the SVG is normalized to 24x24 in the future, remove this scaling
  return <PerfumeSvg {...getIconProps(props)} strokeWidth={getScaledStrokeWidth(props)} />;
};

export const BeautyIcon: React.FC<IconProps> = props => {
  // Note: BeautyIcon uses a custom SVG with viewBox 682x682 (vs standard 24x24)
  // We scale stroke-width proportionally: 1.5 * (500/24) ≈ 31.25
  // This is a one-time calculation - if the SVG is normalized to 24x24 in the future, remove this scaling
  return <BeautySvg {...getIconProps(props)} strokeWidth={getScaledStrokeWidth(props)} />;
};

export const MedicalSuppliesIcon: React.FC<IconProps> = props => {
  return <MedicalSuppliesSvg {...getIconProps(props)} />;
};
export const ConstructionMaterialsIcon: React.FC<IconProps> = props => {
  // Note: ConstructionMaterialsIcon uses a custom SVG with viewBox 511.52x511.52 (vs standard 24x24)
  return <ConstructionSvg {...getIconProps(props)} strokeWidth={getScaledStrokeWidth(props)} />;
};

export const FoodIcon: React.FC<IconProps> = props => {
  return <FoodSvg {...getIconProps(props)} />;
};

export const LiquidIcon: React.FC<IconProps> = props => {
  return <LiquidSvg {...getIconProps(props)} />;
};

export const PlasticIcon: React.FC<IconProps> = props => {
  // Note: PlasticIcon uses a custom SVG with viewBox 512.153x512.153 (vs standard 24x24)
  return <PlasticSvg {...getIconProps(props)} strokeWidth={getScaledStrokeWidth(props)} />;
};

export const FurnitureIcon: React.FC<IconProps> = props => {
  // Note: FurnitureIcon uses a custom SVG with viewBox 682.66669x682.66669 (vs standard 24x24)
  // We scale stroke-width proportionally using the default VIEWBOX_SCALE
  return <FurnitureSvg {...getIconProps(props)} strokeWidth={getScaledStrokeWidth(props)} />;
};

export const ChemicalsIcon: React.FC<IconProps> = props => {
  return <ChemicalsSvg {...getIconProps(props)} />;
};

export const OtherItemIcon: React.FC<IconProps> = ItemMainIcon;

export const ItemIconDynamic: React.FC<IconProps & { itemType: ItemType }> = props => {
  const { itemType, ...rest } = props;
  const IconComponent = getItemIcon(itemType);
  return <IconComponent {...rest} />;
};

// Helper function to get the appropriate item icon component
function getItemIcon(itemType: ItemType): React.FC<IconProps> {
  switch (itemType) {
    case 'perfume':
      return PerfumeIcon;
    case 'beauty':
      return BeautyIcon;
    case 'medical_supplies':
      return MedicalSuppliesIcon;
    case 'construction_materials':
      return ConstructionMaterialsIcon;
    case 'food':
      return FoodIcon;
    case 'liquid':
      return LiquidIcon;
    case 'plastic':
      return PlasticIcon;
    case 'furniture':
      return FurnitureIcon;
    case 'chemicals':
      return ChemicalsIcon;
    case 'other':
      return OtherItemIcon;
    default:
      return ItemMainIcon;
  }
}
